import { useCallback } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { useIntl } from 'react-intl';
import type { useToast } from '@cbhq/cds-web/overlays/useToast';

import { domainMismatchMessage } from './utils/constants';
import {
  INVITE_ERROR_TOAST_MESSAGE_BY_REASON,
  parseInviteErrorReason,
} from './utils/inviteErrorReason';

type UseHandleInviteFailureParams = {
  setEmailError: (message: string | null) => void;
  toast: ReturnType<typeof useToast>;
  genericErrorMessage: MessageDescriptor;
};

/**
 * Shared invite-failure handling for both SingleLEInviteTray and
 * MultiLEInviteTray: RESTRICTED_DOMAIN_MISMATCH becomes an inline email-field
 * error, the other 5 client-actionable reason codes get tailored toast copy,
 * and anything else falls back to the caller's generic error toast.
 */
export function useHandleInviteFailure({
  setEmailError,
  toast,
  genericErrorMessage,
}: UseHandleInviteFailureParams) {
  const { formatMessage } = useIntl();

  return useCallback(
    (error: unknown) => {
      const reason = parseInviteErrorReason(error);
      if (reason === 'RESTRICTED_DOMAIN_MISMATCH') {
        setEmailError(formatMessage(domainMismatchMessage));
        return;
      }
      const reasonMessage =
        reason != null ? INVITE_ERROR_TOAST_MESSAGE_BY_REASON[reason] : undefined;
      toast.show(formatMessage(reasonMessage ?? genericErrorMessage), { variant: 'bgNegative' });
    },
    [formatMessage, genericErrorMessage, setEmailError, toast],
  );
}
